import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Typography, FormControl, Select, MenuItem, Chip, Checkbox, ListItemText,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  fetchProvincesForCountry,
  fetchCitiesForProvince,
} from './slaApiHelpers';

const useStyles = makeStyles((theme) => ({
  formGroup: { marginBottom: theme.spacing(2.5) },
  formLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
    marginBottom: theme.spacing(1),
    display: 'block',
  },
  formControl: {
    width: '100%',
    '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' },
  },
  multiSelect: { backgroundColor: '#f5f5f5' },
  chipGroup: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  provinceChip: { backgroundColor: '#ede7f6', color: '#4527a0' },
  cityChip: { backgroundColor: '#fff3e0', color: '#e65100' },
  helperText: {
    fontSize: '12px',
    color: '#888',
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
}));

const provinceKey = (country, id) => `${country}::${id}`;
const cityEntryKey = (country, provinceId, id) => `${country}::${provinceId}::${id}`;

/**
 * Cascading Province + City multiselects driven by the currently selected
 * countries (and optional warehouse). Province selection is UI-only; only the
 * chosen city IDs are reported back via onCitiesChange to be persisted as
 * destination_cities on the SLA rule.
 *
 * Mirrors the Select Province / Select City flow from the rest of the app
 * (POST /api/address/find/province & /api/address/find/city) but fetches in
 * parallel per country so multiple countries work at once.
 */
const LocationCascadeSelect = ({
  selectedCountries = [],
  warehouse = '',
  selectedCities = [],
  onCitiesChange,
  getCountryName,
  disabled = false,
  /** Optional id → label from address API (e.g. when editing a saved rule before provinces load). */
  prefetchedCityLabels = {},
}) => {
  const classes = useStyles();

  const [provincesByCountry, setProvincesByCountry] = useState({});
  const [citiesByProvince, setCitiesByProvince] = useState({});
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [selectedProvinces, setSelectedProvinces] = useState([]);

  useEffect(() => {
    setProvincesByCountry({});
    setCitiesByProvince({});
    setSelectedProvinces([]);
  }, [warehouse]);

  useEffect(() => {
    let cancelled = false;
    const toFetch = selectedCountries.filter((c) => !(c in provincesByCountry));
    if (toFetch.length === 0) return undefined;

    setLoadingProvinces(true);
    Promise.all(
      toFetch.map((country) =>
        fetchProvincesForCountry({ country, warehouse }).then((list) => [country, list])
      )
    ).then((entries) => {
      if (cancelled) return;
      setProvincesByCountry((prev) => {
        const next = { ...prev };
        entries.forEach(([country, list]) => { next[country] = list; });
        return next;
      });
      setLoadingProvinces(false);
    });
    return () => { cancelled = true; };
  }, [selectedCountries, warehouse, provincesByCountry]);

  useEffect(() => {
    const activeCountries = new Set(selectedCountries);
    setSelectedProvinces((prev) => prev.filter((k) => activeCountries.has(k.split('::')[0])));
  }, [selectedCountries]);

  useEffect(() => {
    let cancelled = false;
    const toFetch = selectedProvinces.filter((k) => !(k in citiesByProvince));
    if (toFetch.length === 0) return undefined;

    setLoadingCities(true);
    Promise.all(
      toFetch.map((key) => {
        const [country, provinceId] = key.split('::');
        return fetchCitiesForProvince({ country, provinceId, warehouse })
          .then((list) => [key, list]);
      })
    ).then((entries) => {
      if (cancelled) return;
      setCitiesByProvince((prev) => {
        const next = { ...prev };
        entries.forEach(([key, list]) => { next[key] = list; });
        return next;
      });
      setLoadingCities(false);
    });
    return () => { cancelled = true; };
  }, [selectedProvinces, warehouse, citiesByProvince]);

  const provinceOptions = useMemo(() => {
    const opts = [];
    selectedCountries.forEach((country) => {
      const list = provincesByCountry[country] || [];
      list.forEach((p) => {
        opts.push({
          key: provinceKey(country, p.id),
          country,
          id: String(p.id),
          name: p.name || p.code || String(p.id),
        });
      });
    });
    return opts;
  }, [selectedCountries, provincesByCountry]);

  const cityOptions = useMemo(() => {
    const opts = [];
    selectedProvinces.forEach((pKey) => {
      const list = citiesByProvince[pKey] || [];
      const [country, provinceId] = pKey.split('::');
      list.forEach((c) => {
        opts.push({
          key: cityEntryKey(country, provinceId, c.id),
          id: String(c.id),
          country,
          provinceId,
          label: c.name_en || c.name || String(c.id),
        });
      });
    });
    return opts;
  }, [selectedProvinces, citiesByProvince]);

  const provinceMetaByKey = useMemo(() => {
    const map = {};
    provinceOptions.forEach((o) => { map[o.key] = o; });
    return map;
  }, [provinceOptions]);

  const allKnownCitiesById = useMemo(() => {
    const map = {};
    Object.entries(citiesByProvince).forEach(([pKey, list]) => {
      const [country, provinceId] = pKey.split('::');
      (list || []).forEach((c) => {
        map[String(c.id)] = {
          id: String(c.id),
          country,
          provinceId,
          label: c.name_en || c.name || String(c.id),
        };
      });
    });
    return map;
  }, [citiesByProvince]);

  useEffect(() => {
    if (!selectedCities.length || !onCitiesChange) return;
    const activeProvinces = new Set(selectedProvinces);
    const pruned = selectedCities.filter((id) => {
      const meta = allKnownCitiesById[id];
      if (!meta) return true;
      return activeProvinces.has(provinceKey(meta.country, meta.provinceId));
    });
    if (pruned.length !== selectedCities.length) onCitiesChange(pruned);
  }, [selectedProvinces, allKnownCitiesById, selectedCities, onCitiesChange]);

  const handleProvinceChange = useCallback((e) => {
    const value = e.target.value;
    setSelectedProvinces(Array.isArray(value) ? value : []);
  }, []);

  const handleCityChange = useCallback((e) => {
    const value = e.target.value;
    if (onCitiesChange) onCitiesChange(Array.isArray(value) ? value : []);
  }, [onCitiesChange]);

  const noCountries = selectedCountries.length === 0;
  const noProvinces = selectedProvinces.length === 0;

  return (
    <>
      <Box className={classes.formGroup}>
        <Typography className={classes.formLabel}>Destination provinces</Typography>
        <FormControl variant="outlined" size="small" className={classes.formControl}>
          <Select
            multiple
            value={selectedProvinces}
            onChange={handleProvinceChange}
            className={classes.multiSelect}
            disabled={disabled || noCountries}
            displayEmpty
            renderValue={(selected) => {
              if (!selected || selected.length === 0) {
                return <span style={{ color: '#999' }}>{noCountries ? 'Select country first' : 'Select provinces (optional)'}</span>;
              }
              return (
                <Box className={classes.chipGroup}>
                  {selected.map((key) => {
                    const meta = provinceMetaByKey[key];
                    const label = meta
                      ? `${meta.name} (${getCountryName(meta.country)})`
                      : key;
                    return <Chip key={key} label={label} size="small" className={classes.provinceChip} />;
                  })}
                </Box>
              );
            }}
          >
            {provinceOptions.length === 0 && !loadingProvinces && (
              <MenuItem disabled value="">
                <em>{noCountries ? 'Select country first' : 'No provinces available'}</em>
              </MenuItem>
            )}
            {provinceOptions.map((opt) => (
              <MenuItem key={opt.key} value={opt.key}>
                <Checkbox checked={selectedProvinces.indexOf(opt.key) > -1} color="primary" />
                <ListItemText
                  primary={opt.name}
                  secondary={getCountryName(opt.country)}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {loadingProvinces && (
          <Typography className={classes.helperText}>
            <CircularProgress size={12} /> Loading provinces…
          </Typography>
        )}
      </Box>

      <Box className={classes.formGroup}>
        <Typography className={classes.formLabel}>Destination cities</Typography>
        <FormControl variant="outlined" size="small" className={classes.formControl}>
          <Select
            multiple
            value={selectedCities}
            onChange={handleCityChange}
            className={classes.multiSelect}
            disabled={disabled || noProvinces}
            displayEmpty
            renderValue={(selected) => {
              if (!selected || selected.length === 0) {
                return <span style={{ color: '#999' }}>{noProvinces ? 'Select province first' : 'Select cities'}</span>;
              }
              return (
                <Box className={classes.chipGroup}>
                  {selected.map((id) => {
                    const key = String(id);
                    const meta = allKnownCitiesById[key];
                    const fallback =
                      prefetchedCityLabels[key] ?? prefetchedCityLabels[id];
                    const label = meta
                      ? `${meta.label} (${getCountryName(meta.country)})`
                      : (fallback != null && fallback !== '' ? fallback : key);
                    return <Chip key={key} label={label} size="small" className={classes.cityChip} />;
                  })}
                </Box>
              );
            }}
          >
            {cityOptions.length === 0 && !loadingCities && (
              <MenuItem disabled value="">
                <em>{noProvinces ? 'Select province first' : 'No cities available'}</em>
              </MenuItem>
            )}
            {cityOptions.map((opt) => (
              <MenuItem key={opt.key} value={opt.id}>
                <Checkbox checked={selectedCities.indexOf(opt.id) > -1} color="primary" />
                <ListItemText
                  primary={opt.label}
                  secondary={getCountryName(opt.country)}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {loadingCities && (
          <Typography className={classes.helperText}>
            <CircularProgress size={12} /> Loading cities…
          </Typography>
        )}
      </Box>
    </>
  );
};

export default LocationCascadeSelect;
